
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { socialService } from "@/services/socialService";
import { Users, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { useAuth } from "@/context/AuthContext";
import { Link } from "@/lib/router-compat";
import { useToast } from "@/components/ui/use-toast";
import T from "@/i18n/T";

const WhoToFollow = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['suggested-users'],
    queryFn: () => socialService.getSuggestedUsers(3),
    enabled: !!user,
  });

  const followMutation = useMutation({
    mutationFn: socialService.followUser,
    onSuccess: (_, followedUserId) => {
      toast({ title: "User followed!" });
      queryClient.invalidateQueries({ queryKey: ['suggested-users'] });
      queryClient.invalidateQueries({ queryKey: ['following', user?.id] });
    },
    onError: () => {
      toast({ variant: "destructive", title: "Failed to follow user." });
    }
  });

  const handleFollow = (userId: string) => {
    followMutation.mutate(userId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" />
          <T k="auto.who-to-follow-ef20" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[120px]" />
                  <Skeleton className="h-3 w-[80px]" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ul className="space-y-4">
            {suggestions && suggestions.length > 0 ? (
              suggestions.map((profile) => (
                <li key={profile.id} className="flex items-center justify-between">
                  <Link to={`/profile?id=${profile.id}`} className="flex items-center space-x-3 group">
                    <ProfileAvatar
                      userId={profile.id}
                      avatarUrl={profile.avatar_url}
                      name={profile.full_name || profile.username}
                      background={profile.avatar_background}
                      size="sm"
                    />
                    <div>
                      <p className="font-semibold text-sm group-hover:underline">{profile.full_name || profile.username}</p>
                      <p className="text-xs text-muted-foreground">@{profile.username}</p>
                    </div>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFollow(profile.id)}
                    disabled={followMutation.isPending && followMutation.variables === profile.id}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    <T k="auto.follow-3903" />
                  </Button>
                </li>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4"><T k="auto.no-new-suggestions-right-now-ad66" /></p>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default WhoToFollow;
